import {
    FormLabel,
    LandmarkIcon,
    LinearProgress,
    IdOptionType,
    ModalContent,
    modalContext,
    SearchableSelect,
    OptionType,
    SelectButtonGroup,
    SelectList,
    Icon,
    Button,
} from "shared";
import {
    defaultBankDetailsFilter,
    ILegalBankDetail,
    ITemplateYandexComponent,
    TLegalCompaniesBlackListByKey,
    YandexListTypes,
} from "entities/template";
import { legalOrgsService } from "entities/legal_orgs";
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { legalOrgsFormContext } from "../../../../lib/providers/legal-orgs-form-provider";
import { AdditionalCompaniesModal } from "./additional_companies_modal/additional_companies_modal";
import { CompaniesConfigurationModal } from "./companies_configuration_modal/companies_configuration_modal";
import type { CompaniesConfigurationScope } from "./companies_configuration_modal/companies_configuration_modal";
import { BankDetailsFilterModal } from "./bank_details_filter_modal/bank_details_filter_modal";
import { LegalBankDetailsModal } from "./legal_bank_details_modal/legal_bank_details_modal";
import countriesJson from "../../../../lib/i18n/countries.json";
import {
    collectSelectableOrgsForLegalYandexDocument,
    collectYaIdsInScopeSelection,
    getScopeCompanyCounts,
} from "../../../../lib/utils/legal_orgs_scope_selection";
import styles from "./legal_company_list.module.scss";


interface ILegalCompanyListProps {
    component: ITemplateYandexComponent;
    onChange: (value: Partial<ITemplateYandexComponent>) => void;
}

const DEFAULT_OPTIONS_LIST = YandexListTypes.yaOrgs;

const pruneCompaniesBlackList = (
    rec: TLegalCompaniesBlackListByKey | undefined,
    validKeys: string[]
): TLegalCompaniesBlackListByKey | undefined => {
    if (!rec) return undefined;
    const next: TLegalCompaniesBlackListByKey = {};
    for (const k of validKeys) {
        const arr = rec[k];
        if (Array.isArray(arr) && arr.length > 0) {
            next[k] = [...arr];
        }
    }
    return Object.keys(next).length > 0 ? next : undefined;
};

export const LegalCompanyList: React.FC<ILegalCompanyListProps> = React.memo(({
                                                                                  component,
                                                                                  onChange,
                                                                              }) => {
    const t = useComponentsTranslations();
    const { close, openModal } = useContext(modalContext);
    const { bisGroups, bisUnits, countries, orgs, loading, extUserId: legalOrgsExtUserId } = useContext(legalOrgsFormContext);
    const { i18n } = useTranslation();

    const optionsList = (component.optionsList as YandexListTypes) || DEFAULT_OPTIONS_LIST;
    const [buttonGroup, setButtonGroup] = useState<ReactNode>();
    const [options, setOptions] = useState<IdOptionType[]>([]);
    const [previousComponentSettings, setPreviousComponentSettings] = useState<Record<YandexListTypes, ITemplateYandexComponent | undefined>>({
        yaBGs: undefined,
        yaBUs: undefined,
        yaCountries: undefined,
        yaOrgs: undefined
    });
    const showFilter =
        optionsList === YandexListTypes.yaBGs ||
        optionsList === YandexListTypes.yaBUs ||
        optionsList === YandexListTypes.yaCountries;

    const getCountryNameByCode = (code: string, language: string) => {
        const countryItem = countriesJson.find((c) => c.code === code);

        return countryItem ? countryItem.name[language as "ru" | "en"] : code;
    };

    useEffect(() => {
        if (!component.list) {
            onChange({
                optionsList: component.optionsList,
                list: [getDefaultValue(component.optionsList)],
                defaultValue: undefined,
            });
        }
    }, [component.list, onChange]);

    useEffect(() => {
        const appendScopeCount = (
            baseLabel: string,
            scope: "bg" | "bu" | "country",
            scopeKey: string,
            excluded: string[] | undefined
        ) => {
            const { total, selected } = getScopeCompanyCounts(orgs, scope, scopeKey, excluded);
            return `${baseLabel} (${selected} / ${total})`;
        };

        switch (optionsList) {
            case YandexListTypes.yaBGs:
                setOptions(
                    [...bisGroups]
                        .sort((a, b) => a.value.localeCompare(b.value))
                        .map((bg) => ({
                            value: bg.id,
                            label: appendScopeCount(bg.value, "bg", bg.id, component.bgBlackList?.[bg.id]),
                            id: bg.id,
                        }))
                );
                break;
            case YandexListTypes.yaBUs:
                setOptions(
                    [...bisUnits]
                        .sort((a, b) => a.value.localeCompare(b.value))
                        .map((bu) => ({
                            value: bu.id,
                            label: appendScopeCount(bu.value, "bu", bu.id, component.buBlackList?.[bu.id]),
                            id: bu.id,
                        }))
                );
                break;
            case YandexListTypes.yaCountries:
                setOptions(
                    [...countries]
                        .sort((a, b) => a.localeCompare(b))
                        .map((code) => ({
                            value: code,
                            label: appendScopeCount(
                                getCountryNameByCode(code, i18n.language),
                                "country",
                                code,
                                component.countriesBlackList?.[code]
                            ),
                            id: code,
                        }))
                );
                break;
            case YandexListTypes.yaOrgs:
                setOptions(
                    [...orgs]
                        .sort((a, b) => a.ya_name.localeCompare(b.ya_name))
                        .map((o) => ({ value: o.ya_id, label: o.ya_name, id: o.ya_id }))
                );
                break;
            default:
                setOptions([]);
        }
    }, [
        optionsList,
        bisGroups,
        bisUnits,
        countries,
        orgs,
        i18n.language,
        component.bgBlackList,
        component.buBlackList,
        component.countriesBlackList,
    ]);

    const getDefaultValue = (type: YandexListTypes): string => {
        switch (type) {
            case YandexListTypes.yaBGs:
            case YandexListTypes.yaBUs:
                return "";
            case YandexListTypes.yaCountries:
                return "RU";
            case YandexListTypes.yaOrgs:
                return orgs.filter((o) => o.ya_name === "ООО \"ЯНДЕКС\"")[0]?.ya_id ?? "";
            default:
                return "";
        }
    };

    const getDefaultOrgSelectOptions = useCallback(
        (scopeList: string[] | undefined): OptionType[] => {
            const selectable = collectSelectableOrgsForLegalYandexDocument(orgs, {
                optionsList,
                list: scopeList,
                bgBlackList: component.bgBlackList,
                buBlackList: component.buBlackList,
                countriesBlackList: component.countriesBlackList,
                additionalCompanies: component.additionalCompanies,
                bankDetailsFilter: component.bankDetailsFilter,
            });
            return selectable.map((o) => ({ value: o.ya_id, label: o.ya_name }));
        },
        [
            orgs,
            optionsList,
            component.bgBlackList,
            component.buBlackList,
            component.countriesBlackList,
            component.additionalCompanies,
            component.bankDetailsFilter,
        ]
    );

    const defaultValueOptions = useMemo(
        () => getDefaultOrgSelectOptions(component.list),
        [getDefaultOrgSelectOptions, component.list]
    );

    const onOptionsListChange = (value: YandexListTypes) => {
        const emptyRecord = {
            defaultValue: undefined,
            bgBlackList: undefined,
            buBlackList: undefined,
            countriesBlackList: undefined,
            additionalCompanies: undefined,
            bankDetailsFilter: undefined,
            listLegalBankDetails: undefined
        };
        setPreviousComponentSettings((prevState) => {
            const updatedState = {...prevState};
            updatedState[component.optionsList] = { ...component };
            return updatedState;
        });
        if (!!previousComponentSettings[value]) {
            onChange({...emptyRecord, ...previousComponentSettings[value] as ITemplateYandexComponent});
        } else {
            onChange({
                optionsList: value,
                list: getDefaultValue(value) ? [getDefaultValue(value)] : [],
                ...emptyRecord
            });
        }
    };

    const onSelectListChange = (value: string[]) => {
        const listLegalBankDetails: ILegalBankDetail[] = [];
        let defaultValue: string | undefined;
        const defaultValueOpts = getDefaultOrgSelectOptions(value);

        if (component.listLegalBankDetails) {
            component.listLegalBankDetails.forEach((detail) => listLegalBankDetails.push({ ...detail }));
        }
        if (
            component.defaultValue &&
            defaultValueOpts.some((o) => String(o.value) === String(component.defaultValue))
        ) {
            defaultValue = component.defaultValue;
        }

        const patch: Partial<ITemplateYandexComponent> = {
            list: value,
            listLegalBankDetails: listLegalBankDetails.length > 0 ? listLegalBankDetails : undefined,
            defaultValue,
        };
        if (optionsList === YandexListTypes.yaBGs) {
            patch.bgBlackList = pruneCompaniesBlackList(component.bgBlackList, value);
        } else if (optionsList === YandexListTypes.yaBUs) {
            patch.buBlackList = pruneCompaniesBlackList(component.buBlackList, value);
        } else if (optionsList === YandexListTypes.yaCountries) {
            patch.countriesBlackList = pruneCompaniesBlackList(component.countriesBlackList, value);
        }

        const additional = component.additionalCompanies ?? [];
        if (
            additional.length > 0
            && (optionsList === YandexListTypes.yaBGs
                || optionsList === YandexListTypes.yaBUs
                || optionsList === YandexListTypes.yaCountries)
        ) {
            const inScopeIds = collectYaIdsInScopeSelection(orgs, optionsList, value);
            const nextAdditional = additional.filter((id) => !inScopeIds.has(id));
            if (nextAdditional.length !== additional.length) {
                patch.additionalCompanies = nextAdditional.length > 0 ? nextAdditional : undefined;
            }
        }

        onChange(patch);
    };

    const onRenderButtonGroup = (node: ReactNode) => setButtonGroup(node);

    const getPlainScopeLabel = useCallback(
        (key: string) => {
            if (optionsList === YandexListTypes.yaBGs) {
                return bisGroups.find((g) => String(g.id) === String(key))?.value ?? key;
            }
            if (optionsList === YandexListTypes.yaBUs) {
                return bisUnits.find((u) => String(u.id) === String(key))?.value ?? key;
            }
            if (optionsList === YandexListTypes.yaCountries) {
                return getCountryNameByCode(key, i18n.language);
            }
            return key;
        },
        [optionsList, bisGroups, bisUnits, i18n.language]
    );

    const onBankDetailsSave = (value: ILegalBankDetail) => {
        const updated = component.listLegalBankDetails ? [...component.listLegalBankDetails] : [];
        const idx = updated.findIndex((d) => d.id === value.id);
        if (idx !== -1) updated[idx] = value;
        else updated.push(value);
        onChange({ listLegalBankDetails: updated });
    };

    const openBankDetailsModal = (index: number) => {
        const companyYaId = component.list?.[index];
        if (!companyYaId) return;
        legalOrgsService.getOrg(companyYaId, legalOrgsExtUserId).then((organization) => {
            const detail = component.listLegalBankDetails?.find((d) => d.id === companyYaId);
            openModal(
                <ModalContent close={close} title={t("addBankDetails")} subtitle={organization.ya_name} showButtonGroup={false}>
                    <LegalBankDetailsModal
                        close={close}
                        organization={organization}
                        companyYaId={companyYaId}
                        bankBlackListArray={detail?.bankBlackListArray ?? []}
                        bankDefaultValue={detail?.bankDefaultValue}
                        onSave={onBankDetailsSave}
                    />
                </ModalContent>
            );
        });
    };

    const openCompaniesConfigurationModal = (index: number) => {
        const scopeKey = component.list?.[index];
        if (!scopeKey) return;
        let scope: CompaniesConfigurationScope;
        let field: "bgBlackList" | "buBlackList" | "countriesBlackList";
        if (optionsList === YandexListTypes.yaBGs) {
            scope = "bg";
            field = "bgBlackList";
        } else if (optionsList === YandexListTypes.yaBUs) {
            scope = "bu";
            field = "buBlackList";
        } else {
            scope = "country";
            field = "countriesBlackList";
        }
        const excluded = component[field]?.[scopeKey] ?? [];

        openModal(
            <ModalContent close={close} title={t("companiesConfiguration")} showButtonGroup={false}>
                <CompaniesConfigurationModal
                    close={close}
                    scope={scope}
                    scopeKey={scopeKey}
                    scopeLabel={getPlainScopeLabel(scopeKey)}
                    organizations={orgs}
                    excludedCompanyIds={excluded}
                    onSave={(excludedYaIds) => {
                        const prev: TLegalCompaniesBlackListByKey = { ...(component[field] ?? {}) };
                        if (excludedYaIds.length === 0) {
                            delete prev[scopeKey];
                        } else {
                            prev[scopeKey] = excludedYaIds;
                        }
                        const keys = Object.keys(prev);
                        onChange({
                            [field]: keys.length > 0 ? prev : undefined,
                        } as Partial<ITemplateYandexComponent>);
                    }}
                />
            </ModalContent>
        );
    };

    const openBankDetailsFilterModal = () => {
        openModal(
            <ModalContent close={close} title={t("legalCompanyListBankDetailsRules")} showButtonGroup={false}>
                <BankDetailsFilterModal
                    close={close}
                    initialValue={component.bankDetailsFilter ?? defaultBankDetailsFilter}
                    onSave={(v) => onChange({ bankDetailsFilter: v })}
                />
            </ModalContent>
        );
    };

    const openAdditionalCompaniesModal = () => {
        if (
            optionsList !== YandexListTypes.yaBGs &&
            optionsList !== YandexListTypes.yaBUs &&
            optionsList !== YandexListTypes.yaCountries
        ) {
            return;
        }
        openModal(
            <ModalContent close={close} title={t("legalCompanyListAdditionalOrgs")} showButtonGroup={false}>
                <AdditionalCompaniesModal
                    close={close}
                    allOrgs={orgs}
                    optionsList={optionsList}
                    scopeList={component.list ?? []}
                    additionalCompanyIds={component.additionalCompanies ?? []}
                    onSave={(ids) =>
                        onChange({
                            additionalCompanies: ids.length > 0 ? ids : undefined,
                        })
                    }
                />
            </ModalContent>
        );
    };

    const renderActionButton = () =>
        optionsList === YandexListTypes.yaOrgs ? <LandmarkIcon/> : <Icon glyph="edit" size="m" className={styles.editIcon}/>;

    const radioOptions = [
        { value: YandexListTypes.yaOrgs, content: t("companies") },
        { value: YandexListTypes.yaBGs, content: t("legalYandexListBisGroups") },
        { value: YandexListTypes.yaBUs, content: t("legalYandexListBisUnits") },
        { value: YandexListTypes.yaCountries, content: t("countries") },
    ];

    const additionalCompaniesCount = component.additionalCompanies?.length ?? 0;

    const selectListAddButtonLabel = useMemo(() => {
        switch (optionsList) {
            case YandexListTypes.yaOrgs:
                return t("legalCompanyListAddCompany");
            case YandexListTypes.yaBUs:
                return t("legalCompanyListAddBusinessUnit");
            case YandexListTypes.yaBGs:
                return t("legalCompanyListAddBusinessGroup");
            case YandexListTypes.yaCountries:
                return t("legalCompanyListAddCountry");
            default:
                return undefined;
        }
    }, [optionsList, t]);

    return (
        <div className={styles.container}>
            <FormLabel title={t("list")}>
                {!loading && (
                    <>
                        <div className={styles.listActionButtons}>{buttonGroup}</div>
                        <SelectButtonGroup
                            className={styles.radioGroup}
                            options={radioOptions}
                            selectedValue={optionsList}
                            onChange={(val) => onOptionsListChange(val as YandexListTypes)}
                        />
                        {showFilter && (
                            <div className={styles.scopeOutlineActions}>
                                <Button size="m" view="outline" type="button" onClick={openBankDetailsFilterModal}>
                                    <div className={styles.outlineActionButtonInner}>
                                        <Icon glyph="filterAlternative" />
                                        <span>{t("legalCompanyListBankDetailsRules")}</span>
                                    </div>
                                </Button>
                                <Button size="m" view="outline" type="button" onClick={openAdditionalCompaniesModal}>
                                    <div className={styles.outlineActionButtonInner}>
                                        <Icon glyph="plus" />
                                        <span>
                                            {t("legalCompanyListAdditionalOrgs")} ({additionalCompaniesCount})
                                        </span>
                                    </div>
                                </Button>
                            </div>
                        )}
                        <SelectList
                            value={component.list}
                            actionButton={renderActionButton()}
                            options={options}
                            resetFactor={component.id}
                            renderButtonGroup={onRenderButtonGroup}
                            onActionButtonClick={optionsList === YandexListTypes.yaOrgs ? openBankDetailsModal : openCompaniesConfigurationModal}
                            onChange={onSelectListChange}
                            addButtonLabel={selectListAddButtonLabel}
                        />
                        <FormLabel title={t("defaultValue")} className={styles.defaultValueSelect}>
                            <SearchableSelect
                                options={defaultValueOptions}
                                optionValue={
                                    component.defaultValue
                                        ? (defaultValueOptions.find(
                                              (o) => String(o.value) === String(component.defaultValue)
                                          ) as OptionType)
                                        : undefined
                                }
                                onChange={(val) => onChange({ defaultValue: val ? (val as string) : undefined })}
                                isMulti={false}
                                hideClearIcon={false}
                                isSearchable={true}
                            />
                        </FormLabel>
                    </>
                )}
                {loading && <LinearProgress className={styles.progressBar}/>}
            </FormLabel>
        </div>
    );
});
